// client/src/pages/AdminDashboard.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// Icons
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  Shield, 
  FileText, 
  Plus, 
  LogOut, 
  Search, 
  MoreVertical,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit2,
  Trash2,
  Save,
  X,
  PlayCircle
} from "lucide-react";
import Logo from "../assets/logo.png"; // Ensure path matches

function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("certifications");

  const [exams, setExams] = useState([]);
  const [editingExamId, setEditingExamId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    slug: "",
    description: "",
    price: "",
    durationMinutes: "",
    passScore: "",
  });
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [grantForm, setGrantForm] = useState({
    userEmail: "",
    examId: "",
    nameOnCertificate: "",
  });

  const [form, setForm] = useState({
    title: "",
    slug: "",
    description: "",
    price: "",
    durationMinutes: 60,
    totalQuestions: 0,
    passScore: 60,
  });

  const admin = JSON.parse(localStorage.getItem("user") || "{}");

  // --- LOGIC (PRESERVED) ---
  useEffect(() => {
    if (!admin || admin.role !== "admin") {
      navigate("/dashboard");
    }
  }, [admin, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (activeTab === "certifications") {
          const res = await api.get("/api/admin/exams");
          setExams(res.data);
        } else if (activeTab === "users") {
          const res = await api.get("/api/admin/users");
          setUsers(res.data);
        } else if (activeTab === "payments") {
          const res = await api.get("/api/admin/payments");
          setPayments(res.data);
        } else if (activeTab === "access") {
          const [attemptRes, examRes] = await Promise.all([
            api.get("/api/admin/attempts"),
            api.get("/api/admin/exams"),
          ]);
          setAttempts(attemptRes.data);
          setExams(examRes.data);
        }
      } catch (err) {
        console.error("Admin fetch error:", err.response?.data || err.message);
      }
    };

    fetchData();
  }, [activeTab]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateExam = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title,
        slug: form.slug,
        description: form.description,
        price: Number(form.price),
        durationMinutes: Number(form.durationMinutes),
        totalQuestions: Number(form.totalQuestions),
        passScore: Number(form.passScore),
      };

      const res = await api.post("/api/exams", payload);
      alert("Certification created");
      setExams((prev) => [res.data, ...prev]);
      setForm({
        title: "",
        slug: "",
        description: "",
        price: "",
        durationMinutes: 60,
        totalQuestions: 0,
        passScore: 60,
      });
    } catch (err) {
      console.error("Create exam error:", err.response?.data || err.message);
      alert("Failed to create certification");
    }
  };

  const handleToggleActive = async (exam) => {
    try {
      const res = await api.patch(`/api/exams/${exam._id}`, {
        isActive: !exam.isActive,
      });
      setExams((prev) =>
        prev.map((e) => (e._id === exam._id ? res.data : e))
      );
    } catch (err) {
      console.error("Toggle active error:", err.response?.data || err.message);
      alert("Failed to update status");
    }
  };

  const handleGrantFormChange = (e) => {
    const { name, value } = e.target;
    setGrantForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/api/admin/attempts/grant", grantForm);
      setAttempts((prev) => [res.data, ...prev]);
      setGrantForm({ userEmail: "", examId: "", nameOnCertificate: "" });
      alert("Access granted");
    } catch (err) {
      console.error("Grant access error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to grant access");
    }
  };

  const handleRevokeAccess = async (attemptId) => {
    if (!window.confirm("Revoke this access?")) return;
    try {
      await api.post(`/api/admin/attempts/${attemptId}/revoke`);
      setAttempts((prev) =>
        prev.map((a) =>
          a._id === attemptId ? { ...a, revoked: true } : a
        )
      );
    } catch (err) {
      console.error("Revoke access error:", err.response?.data || err.message);
      alert("Failed to revoke access");
    }
  };

  const handleDeleteAttempt = async (attemptId) => {
    if (!window.confirm("Delete this attempt record? This cannot be undone.")) return;
    try {
      await api.delete(`/api/admin/attempts/${attemptId}`);
      setAttempts((prev) => prev.filter((a) => a._id !== attemptId));
    } catch (err) {
      console.error("Delete attempt error:", err.response?.data || err.message);
      alert("Failed to delete attempt");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment record? This cannot be undone.")) return;
    try {
      await api.delete(`/api/admin/payments/${paymentId}`);
      setPayments((prev) => prev.filter((p) => p._id !== paymentId));
    } catch (err) {
      console.error("Delete payment error:", err.response?.data || err.message);
      alert("Failed to delete payment");
    }
  };

  const handleApproveAttempt = async (attemptId) => {
    try {
      const res = await api.post(`/api/admin/attempts/${attemptId}/approve`);
      setAttempts((prev) =>
        prev.map((a) => (a._id === attemptId ? res.data : a))
      );
      alert("Attempt approved & certificate issued.");
    } catch (err) {
      console.error("Approve attempt error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to approve attempt");
    }
  };

  const handleFailAttempt = async (attemptId) => {
    if (!window.confirm("Mark this attempt as failed?")) return;
    try {
      const res = await api.post(`/api/admin/attempts/${attemptId}/fail`);
      setAttempts((prev) =>
        prev.map((a) => (a._id === attemptId ? res.data : a))
      );
    } catch (err) {
      console.error("Fail attempt error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to mark attempt as failed");
    }
  };

  // NEW: delete certification
  const handleDeleteExam = async (examId) => {
    if (!window.confirm("Delete this certification? This will remove exam, questions and related config. This action cannot be undone.")) return;
    try {
      await api.delete(`/api/admin/exams/${examId}`);
      setExams((prev) => prev.filter((e) => e._id !== examId));
      // if currently editing this exam, cancel edit
      if (editingExamId === examId) {
        setEditingExamId(null);
        setEditForm({
          title: "",
          slug: "",
          description: "",
          price: "",
          durationMinutes: "",
          passScore: "",
        });
      }
      alert("Certification deleted");
    } catch (err) {
      console.error("Delete exam error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to delete certification");
    }
  };

  // NEW: save edited exam (title/slug/description/price/duration/passScore)
  const handleSaveEdit = async (examId) => {
    try {
      const payload = {
        title: editForm.title,
        slug: editForm.slug,
        description: editForm.description,
        price: Number(editForm.price),
        durationMinutes: Number(editForm.durationMinutes),
        passScore: Number(editForm.passScore),
      };
      const res = await api.put(`/api/admin/exams/${examId}`, payload);
      setExams((prev) => prev.map((ex) => (ex._id === examId ? res.data : ex)));
      setEditingExamId(null);
      setEditForm({
        title: "",
        slug: "",
        description: "",
        price: "",
        durationMinutes: "",
        passScore: "",
      });
      alert("Certification updated");
    } catch (err) {
      console.error("Update exam error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to update certification");
    }
  };

  // --- END LOGIC ---

  // --- UI RENDERERS ---

  const renderCertificationsTab = () => (
    <div className="space-y-8 animate-in fade-in duration-500">
      
      {/* Create New Card */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Plus size={20} className="text-orange-500"/> Create Certification
        </h3>
        <form onSubmit={handleCreateExam} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="col-span-1 md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Title</label>
            <input name="title" value={form.title} onChange={handleFormChange} required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Slug</label>
            <input name="slug" value={form.slug} onChange={handleFormChange} required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Price (₹)</label>
            <input type="number" name="price" value={form.price} onChange={handleFormChange} required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none transition-all" />
          </div>
          <div className="col-span-1 md:col-span-4">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleFormChange} rows={2} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Duration (min)</label>
            <input type="number" name="durationMinutes" value={form.durationMinutes} onChange={handleFormChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Total Questions</label>
            <input type="number" name="totalQuestions" value={form.totalQuestions} onChange={handleFormChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Pass Score (%)</label>
            <input type="number" name="passScore" value={form.passScore} onChange={handleFormChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none transition-all" />
          </div>
          <div className="flex items-end">
             <button type="submit" className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors font-medium w-full">Create</button>
          </div>
        </form>
      </div>

      {/* List Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
           <h3 className="text-lg font-bold text-gray-800">Existing Certifications</h3>
           <span className="text-sm text-gray-400">{exams.length} Records</span>
        </div>
        
        {exams.length === 0 ? (
           <div className="p-8 text-center text-gray-400">No certifications found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Slug</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Price</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Config</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {exams.map((exam) => {
                  const isEditing = editingExamId === exam._id;
                  return (
                    <tr key={exam._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        {isEditing ? (
                          <input className="w-full border rounded px-2 py-1 text-sm" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} />
                        ) : (
                          <span className="font-medium text-gray-900">{exam.title}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {isEditing ? <input className="w-full border rounded px-2 py-1 text-sm" value={editForm.slug} onChange={(e) => setEditForm((f) => ({ ...f, slug: e.target.value }))} /> : exam.slug}
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-600">
                        {isEditing ? <input type="number" className="w-20 border rounded px-2 py-1 text-sm" value={editForm.price} onChange={(e) => setEditForm((f) => ({ ...f, price: e.target.value }))} /> : `₹${exam.price}`}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {isEditing ? (
                           <div className="flex flex-col gap-2">
                             <div className="flex gap-2">
                               <input type="number" placeholder="Duration min" className="w-24 border rounded px-2 py-1 text-sm" value={editForm.durationMinutes} onChange={(e) => setEditForm((f) => ({ ...f, durationMinutes: e.target.value }))} />
                               <input type="number" placeholder="Pass %" className="w-24 border rounded px-2 py-1 text-sm" value={editForm.passScore} onChange={(e) => setEditForm((f) => ({ ...f, passScore: e.target.value }))} />
                             </div>
                             <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} />
                           </div>
                        ) : (
                          <div>
                            <div>{exam.durationMinutes}m • {exam.passScore}% Pass</div>
                            <div className="text-xs text-gray-500 mt-1 line-clamp-2">{exam.description || "—"}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                         <span className={`px-2 py-1 rounded-full text-xs font-bold ${exam.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                            {exam.isActive ? "Active" : "Inactive"}
                         </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-2">
                             <button onClick={() => handleSaveEdit(exam._id)} className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100" title="Save"><Save size={16}/></button>
                             <button onClick={() => setEditingExamId(null)} className="p-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100" title="Cancel"><X size={16}/></button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                             <button onClick={() => {
                                setEditingExamId(exam._id);
                                setEditForm({ 
                                  title: exam.title || "", 
                                  slug: exam.slug || "", 
                                  description: exam.description || "",
                                  price: exam.price || "", 
                                  durationMinutes: exam.durationMinutes || "", 
                                  passScore: exam.passScore || "" 
                                });
                             }} className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors" title="Edit"><Edit2 size={16}/></button>
                             
                             <button onClick={() => handleToggleActive(exam)} className={`p-2 rounded-lg transition-colors ${exam.isActive ? "text-green-600 hover:bg-red-50 hover:text-red-600" : "text-gray-400 hover:bg-green-50 hover:text-green-600"}`} title="Toggle Active">
                                {exam.isActive ? <CheckCircle size={16}/> : <XCircle size={16}/>}
                             </button>

                             <button onClick={() => navigate(`/admin/exams/${exam._id}/questions`)} className="px-3 py-1 bg-gray-900 text-white text-xs rounded-lg hover:bg-gray-700 transition-colors" title="Manage Questions">
                                Questions
                             </button>

                             {/* New: Delete exam */}
                             <button onClick={() => handleDeleteExam(exam._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete Certification">
                               <Trash2 size={16}/>
                             </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderUsersTab = () => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-in fade-in duration-500">
      <div className="p-6 border-b border-gray-100">
         <h3 className="text-lg font-bold text-gray-800">Registered Users</h3>
      </div>
      {users.length === 0 ? <div className="p-8 text-center text-gray-400">No users found.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">User</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Email</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Role</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Last Login</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr key={u._id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">{u.name}</td>
                  <td className="px-6 py-4 text-gray-500">{u.email}</td>
                  <td className="px-6 py-4">
                     <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${u.role === 'admin' ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                        {u.role}
                     </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderPaymentsTab = () => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-in fade-in duration-500">
      <div className="p-6 border-b border-gray-100">
         <h3 className="text-lg font-bold text-gray-800">Transaction History</h3>
      </div>
      {payments.length === 0 ? <div className="p-8 text-center text-gray-400">No payments found.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">User</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Certification</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Date</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {payments.map((p) => (
                <tr key={p._id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                     <div className="font-medium text-gray-900">{p.userId?.name}</div>
                     <div className="text-xs text-gray-500">{p.userId?.email}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{p.examId?.title}</td>
                  <td className="px-6 py-4 font-mono font-medium">₹{p.amount}</td>
                  <td className="px-6 py-4">
                     <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${p.status === 'captured' ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {p.status}
                     </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{new Date(p.createdAt).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleDeletePayment(p._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                       <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderAccessTab = () => (
    <div className="space-y-8 animate-in fade-in duration-500">
      
      {/* Grant Form */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Shield size={20} className="text-orange-500"/> Grant Access Manual
        </h3>
        <form onSubmit={handleGrantAccess} className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">User Email</label>
            <input name="userEmail" value={grantForm.userEmail} onChange={handleGrantFormChange} required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none" />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Certification</label>
            <select name="examId" value={grantForm.examId} onChange={handleGrantFormChange} required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none">
              <option value="">Select exam</option>
              {exams.map((e) => <option key={e._id} value={e._id}>{e.title}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Cert Name (Opt)</label>
            <input name="nameOnCertificate" value={grantForm.nameOnCertificate} onChange={handleGrantFormChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 outline-none" />
          </div>
          <button type="submit" className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors font-medium">Grant Access</button>
        </form>
      </div>

      {/* Access Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
           <h3 className="text-lg font-bold text-gray-800">Access & Attempts</h3>
        </div>
        {attempts.length === 0 ? <div className="p-8 text-center text-gray-400">No records found.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Candidate</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Exam</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Proctor Flags</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Source</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attempts.map((a) => (
                  <tr key={a._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                       <div className="font-medium text-gray-900">{a.userId?.name}</div>
                       <div className="text-xs text-gray-500">{a.userId?.email}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 text-sm">{a.examId?.title}</td>
                    <td className="px-6 py-4">
                       <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize 
                          ${a.revoked ? "bg-red-100 text-red-700" 
                          : a.status === 'passed' ? "bg-green-100 text-green-700"
                          : a.status === 'under-review' ? "bg-orange-100 text-orange-700"
                          : "bg-gray-100 text-gray-600"}`}>
                          {a.revoked ? "Revoked" : a.status}
                       </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                       {typeof a.score === "number" ? `${a.score.toFixed(1)}%` : "-"}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                       {a.proctoring ? (
                          <div className="flex gap-2">
                             <span className="bg-gray-100 px-1.5 rounded">Tabs: {a.proctoring.tabSwitchCount || 0}</span>
                             <span className="bg-gray-100 px-1.5 rounded">FS: {a.proctoring.fullscreenExitCount || 0}</span>
                          </div>
                       ) : "-"}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500 uppercase">{a.paymentId ? "Paid" : "Admin"}</td>
                    <td className="px-6 py-4 text-right">
                       <div className="flex justify-end gap-2">
                          {!a.revoked && a.status === "under-review" && (
                            <>
                              <button onClick={() => handleApproveAttempt(a._id)} className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100" title="Approve"><CheckCircle size={16}/></button>
                              <button onClick={() => handleFailAttempt(a._id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100" title="Fail"><XCircle size={16}/></button>
                            </>
                          )}
                          <button onClick={() => handleRevokeAccess(a._id)} disabled={a.revoked} className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg disabled:opacity-30" title="Revoke"><AlertCircle size={16}/></button>
                          <button onClick={() => handleDeleteAttempt(a._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 size={16}/></button>
                       </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // --- MAIN LAYOUT ---
  const navItems = [
    { id: 'certifications', label: 'Certifications', icon: FileText },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'access', label: 'Access Control', icon: Shield },
  ];

  return (
    <div className="flex h-screen w-full bg-gray-50 font-sans">
       
       {/* Sidebar */}
       <aside className="w-64 bg-gray-900 text-gray-300 flex flex-col shrink-0">
          <div className="h-20 flex items-center px-6 border-b border-gray-800">
             <img src={Logo} alt="Orivon" className="h-8 w-auto mr-3" />
             <span className="text-white font-bold tracking-tight">ADMIN</span>
          </div>
          
          <nav className="flex-1 p-4 space-y-1">
             {navItems.map((item) => (
                <button
                   key={item.id}
                   onClick={() => setActiveTab(item.id)}
                   className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeTab === item.id 
                      ? "bg-orange-600 text-white shadow-lg shadow-orange-900/20" 
                      : "hover:bg-gray-800 hover:text-white"
                   }`}
                >
                   <item.icon size={20} /> {item.label}
                </button>
             ))}
          </nav>

          <div className="p-4 border-t border-gray-800">
             <div className="px-4 py-3 mb-2">
                <p className="text-xs text-gray-500 uppercase">Logged in as</p>
                <p className="text-white font-medium truncate">{admin.name}</p>
             </div>
             <button onClick={() => { localStorage.clear(); window.location.href = "/"; }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-900/30 text-gray-400 hover:text-red-400 transition-colors">
                <LogOut size={20} /> Logout
             </button>
          </div>
       </aside>

       {/* Main Content */}
       <main className="flex-1 flex flex-col h-screen overflow-hidden">
          <header className="h-20 bg-white border-b border-gray-200 flex items-center justify-between px-8 shrink-0">
             <h2 className="text-xl font-bold text-gray-800 capitalize">{activeTab.replace("-", " ")} Overview</h2>
             <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString()}</span>
             </div>
          </header>

          <div className="flex-1 overflow-y-auto p-8">
             <div className="max-w-6xl mx-auto">
                {activeTab === "certifications" && renderCertificationsTab()}
                {activeTab === "users" && renderUsersTab()}
                {activeTab === "payments" && renderPaymentsTab()}
                {activeTab === "access" && renderAccessTab()}
             </div>
          </div>
       </main>
    </div>
  );
}

export default AdminDashboard;
